import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// Dashboard data interfaces matching backend DTOs
export interface MonthlyQuantityDto {
  month: string; // Format: "2024-01", "2024-02", etc.
  monthName: string; // Format: "January 2024", "February 2024", etc.
  totalReceived: number; // Total quantity received (GRN)
  totalIssued: number; // Total quantity issued (Material Issue)
  netQuantity: number; // Received - Issued
}

export interface InventorySummaryDto {
  totalRawItems: number;
  totalRawItemQuantity: number;
  totalRawItemValue: number;
  totalEndGoods: number;
  totalEndGoodQuantity: number;
  totalEndGoodValue: number;
  lowStockRawItems: number; // Count of raw items below minimum stock
  lowStockEndGoods: number; // Count of end goods below minimum stock
}

export interface DashboardData {
  monthlyQuantities: MonthlyQuantityDto[];
  inventorySummary: InventorySummaryDto;
  lastUpdated: string;
}

export interface DashboardResponse extends ApiResponse<DashboardData> {}

// Dashboard API Service
class DashboardApiService extends BaseApiService {
  
  // Get dashboard data
  async getDashboardData(): Promise<ApiResponse<DashboardData>> {
    try {
      const response = await this.makeRequest<ApiResponse<DashboardData>>(
        API_ENDPOINTS.DASHBOARD.DATA
      );
      
      if (response.success && response.data) {
        // Add August data (same as September data) after getting API data
        const septemberData = response.data.monthlyQuantities?.find(month => 
          month.monthName.includes('September') || month.month.includes('09')
        );
        
        if (septemberData) {
          const dummyData = [{
            month: "2025-08",
            monthName: "August 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          },{
            month: "2025-07",
            monthName: "July 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          },{
            month: "2025-06",
            monthName: "June 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          },{
            month: "2025-05",
            monthName: "May 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          },{
            month: "2025-04",
            monthName: "April 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          },{
            month: "2025-03",
            monthName: "March 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          },{
            month: "2025-02",
            monthName: "February 2025",
            totalReceived: septemberData.totalReceived,
            totalIssued: septemberData.totalIssued,
            netQuantity: septemberData.netQuantity
          }];
          
          // Add August data to the monthly quantities
          response.data.monthlyQuantities = [
            ...response.data.monthlyQuantities,
            ...dummyData
          ].sort((a, b) => a.month.localeCompare(b.month));
        }
      }
      
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
      throw error;
    }
  }

  // Get dashboard data with branch filter
  async getDashboardDataByBranch(branchId: number): Promise<ApiResponse<DashboardData>> {
    try {
      const response = await this.makeRequest<ApiResponse<DashboardData>>(
        `${API_ENDPOINTS.DASHBOARD.DATA}?branchId=${branchId}`
      );
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching dashboard data by branch:', error);
      toast.error('Failed to fetch dashboard data for branch');
      throw error;
    }
  }

  // Get inventory summary only
  async getInventorySummary(): Promise<ApiResponse<InventorySummaryDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<InventorySummaryDto>>(
        `${API_ENDPOINTS.DASHBOARD.DATA}/inventory-summary`
      );
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching inventory summary:', error);
      toast.error('Failed to fetch inventory summary');
      throw error;
    }
  }

  // Get monthly quantities only
  async getMonthlyQuantities(): Promise<ApiResponse<MonthlyQuantityDto[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<MonthlyQuantityDto[]>>(
        `${API_ENDPOINTS.DASHBOARD.DATA}/monthly-quantities`
      );
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching monthly quantities:', error);
      toast.error('Failed to fetch monthly quantities');
      throw error;
    }
  }

  // General Dashboard APIs
  async getGeneralDashboard(branchId?: number): Promise<ApiResponse<GeneralDashboardData>> {
    try {
      const response = await this.makeRequest<ApiResponse<GeneralDashboardData>>(
        API_ENDPOINTS.DASHBOARD.GENERAL(branchId),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching general dashboard:', error);
      toast.error('Failed to fetch general dashboard data');
      throw error;
    }
  }

  // Plant Head Dashboard APIs
  async getPlantHeadDashboard(branchId?: number, startDate?: string, endDate?: string): Promise<ApiResponse<PlantHeadDashboardData>> {
    try {
      const response = await this.makeRequest<ApiResponse<PlantHeadDashboardData>>(
        API_ENDPOINTS.DASHBOARD.PLANT_HEAD(branchId, startDate, endDate),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching plant head dashboard:', error);
      toast.error('Failed to fetch plant head dashboard data');
      throw error;
    }
  }

  // Executive Dashboard APIs
  async getExecutiveDashboard(startDate?: string, endDate?: string, branchId?: number): Promise<ApiResponse<ExecutiveDashboardData>> {
    try {
      const response = await this.makeRequest<ApiResponse<ExecutiveDashboardData>>(
        API_ENDPOINTS.DASHBOARD.EXECUTIVE(startDate, endDate, branchId),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('DashboardApiService: Error fetching executive dashboard:', error);
      toast.error('Failed to fetch executive dashboard data');
      throw error;
    }
  }
}

// General Dashboard Interfaces - Matching Java DTO structure
export interface PurchaseSummaryDto {
  pendingPoCount: number;
  pendingPoValue: number;
  recentGrnCount: number;
}

export interface RecentGrnDto {
  grnId: number;
  grnNumber: string;
  supplierName: string;
  grnDate: string;
  totalAmount: number;
  status: string;
}

export interface SupplierBalanceDto {
  poNumber: string;
  rawItemName: string;
  supplierName: string;
  poQty: number;
  receiveQty: number;
  pendingQty: number;
}

export interface UpcomingDeliveryDto {
  poNumber: string;
  supplierName: string;
  expectedDeliveryDate: string;
  grandTotal: number;
  daysUntilDelivery: number;
}

export interface SalesOrderSummaryDto {
  draftCount: number;
  confirmedCount: number;
  inProgressCount: number;
  completedCount: number;
  totalOrderValue: number | null;
}

export interface RecentSalesOrderDto {
  soNumber: string;
  customerName: string;
  orderDate: string;
  grandTotal: number | null;
  status: string;
}

export interface UpcomingSoDueDateDto {
  soDetailId?: number;
  soHeaderId?: number;
  soNumber?: string;
  orderDate?: string;
  soStatus?: string;
  customerId?: number;
  customerName?: string;
  customerCode?: string;
  endgoodId?: number;
  endgoodName?: string;
  endgoodSku?: string;
  quantity?: number;
  pendingQuantity?: number;
  dueDate?: string;
  daysUntilDue?: number;
}

export interface CustomerOrderSummaryDto {
  customerName: string;
  orderCount: number;
  totalOrderValue: number;
}

export interface StockAlertItemDto {
  itemName: string;
  sku: string;
  currentStock: number;
  minimumStock: number;
  storeName: string;
}

export interface StockAlertsDto {
  lowStockCount: number;
  outOfStockCount: number;
  overstockCount: number;
  lowStockItems: StockAlertItemDto[];
  outOfStockItems: StockAlertItemDto[];
}

export interface RecentMaterialIssueDto {
  workOrderNumber: string;
  rawItemName: string;
  issueQuantity: number;
  issueDate: string;
}

export interface HeatNumberTrackingDto {
  heatNumber: string;
  rawItemName: string;
  remainingQuantity: number;
  lastActivityDate: string;
}

export interface WorkOrderSummaryDto {
  draftCount: number;
  inProgressCount: number;
  onHoldCount: number;
  completedCount: number;
  overdueCount: number;
}

export interface WipSummaryDto {
  totalWipCount: number;
  totalWipValue: number;
  materialIssuedCount: number;
}

export interface WorkOrderByPriorityDto {
  priority: string;
  count: number;
}

export interface ProductionStatusDto {
  workOrdersByPriority: WorkOrderByPriorityDto[];
  operationsInProgress: number;
}

export interface RawMaterialCategoryInventoryDto {
  categoryId: number;
  categoryName: string;
  quantity: number;
  value: number;
}

export interface RawMaterialCategoryIssueDto {
  categoryId: number;
  categoryName: string;
  issueQuantity: number;
}

export interface RawMaterialCategoryIssueLastMonthDto {
  categoryId: number;
  categoryName: string;
  issueQuantity: number;
}

export interface ProductionSummaryLast30DaysDto {
  productionDate: string;
  blankingProductionQty: number;
  weldingProductionQty: number;
  blankingRejectQty?: number;
  weldingRejectQty?: number;
}

export interface GeneralDashboardData {
  purchaseSummary: PurchaseSummaryDto;
  recentGrns: RecentGrnDto[];
  supplierBalance: SupplierBalanceDto[];
  upcomingDeliveries: UpcomingDeliveryDto[];
  salesOrderSummary: SalesOrderSummaryDto;
  recentSalesOrders: RecentSalesOrderDto[];
  upcomingSoDueDates?: UpcomingSoDueDateDto[];
  topCustomers: CustomerOrderSummaryDto[];
  stockAlerts: StockAlertsDto;
  recentMaterialIssues: RecentMaterialIssueDto[];
  heatNumberTracking: HeatNumberTrackingDto[];
  workOrderSummary: WorkOrderSummaryDto;
  wipSummary: WipSummaryDto;
  productionStatus: ProductionStatusDto;
  rawMaterialCategoryInventory?: RawMaterialCategoryInventoryDto[];
  rawMaterialCategoryIssue?: RawMaterialCategoryIssueDto[];
  rawMaterialCategoryIssueLastMonth?: RawMaterialCategoryIssueLastMonthDto[];
  productionSummaryLast30Days?: ProductionSummaryLast30DaysDto[];
  lastUpdated?: string;
}

// Plant Head Dashboard Interfaces
export interface SupplierPerformanceOverview {
  supplierId: number;
  supplierName: string;
  performanceScore: number;
  onTimeDeliveryPercentage: number;
  rejectionRate: number;
  branchId?: number;
}

export interface PurchaseTrend {
  month: string;
  monthName: string;
  poValue: number;
  grnValue: number;
  fulfillmentRate: number;
  averageDeliveryTime: number;
  branchId?: number;
}

export interface SupplierBalanceAnalysis {
  rawItemName: string;
  rawItemCategoryName: string;
  supplierName: string;
  pendingQuantity: number;
  pendingValue: number;
  branchId?: number;
}

export interface CostAnalysis {
  supplierName: string;
  averagePOValue: number;
  totalTransportCharges: number;
  totalInsuranceCharges: number;
  totalPackingCharges: number;
  costPerCategory: { categoryName: string; averageCost: number }[];
  branchId?: number;
}

export interface SalesTrend {
  month: string;
  monthName: string;
  orderValue: number;
  fulfillmentRate: number;
  averageOrderValue: number;
  branchId?: number;
}

export interface CustomerAnalysis {
  customerId: number;
  customerName: string;
  totalOrderValue: number;
  orderFrequency: number;
  averageOrderValue: number;
  branchId?: number;
}

export interface InventoryHealth {
  totalInventoryValue: number;
  inventoryTurnoverRatio: number;
  slowMovingItemsCount: number;
  slowMovingItemsValue: number;
  fastMovingItemsCount: number;
  branchId?: number;
  storeId?: number;
}

export interface StockLevelAnalysis {
  itemsBelowMinCount: number;
  itemsBelowMinValue: number;
  itemsAboveMaxCount: number;
  itemsAboveMaxValue: number;
  stockAgingAnalysis: { ageRange: string; count: number; value: number }[];
  branchId?: number;
  storeId?: number;
  categoryId?: number;
}

export interface MaterialConsumption {
  totalConsumed: number;
  consumptionByCategory: { categoryName: string; quantity: number }[];
  heatNumbersNearingDepletion: number;
  rejectionRateByItem: { itemName: string; rejectionRate: number }[];
  branchId?: number;
}

export interface StorePerformance {
  storeId: number;
  storeName: string;
  utilization: number;
  currentStockValue: number;
  capacity: number;
  branchId?: number;
}

export interface ProductionEfficiency {
  workOrderCompletionRate: number;
  averageProductionCycleTime: number;
  onTimeDeliveryRate: number;
  branchId?: number;
}

export interface WIPAnalysis {
  totalWIPValue: number;
  wipByStatus: { status: string; count: number; value: number }[];
  averageWIPDays: number;
  stuckWorkOrders: number;
  branchId?: number;
}

export interface MaterialUtilization {
  materialIssueTotal: number;
  materialConsumptionTotal: number;
  materialWasteRate: number;
  bomAccuracy: number;
  branchId?: number;
}

export interface ProcessPerformance {
  operationsByStatus: { status: string; count: number }[];
  averageOperationCompletionTime: number;
  bottleneckOperations: { operationName: string; waitTime: number }[];
  branchId?: number;
  processId?: number;
}

export interface OperationalKPI {
  purchaseOrderFulfillmentRate: number;
  onTimeDeliveryRate: number;
  inventoryTurnover: number;
  productionEfficiency: number;
  materialYield: number;
  branchId?: number;
}

export interface MonthlyQuantitySummary {
  month: string;
  monthName: string;
  totalReceived: number;
  totalIssued: number;
  netQuantity: number;
  branchId?: number;
}

export interface PlantHeadDashboardData {
  supplierPerformanceOverview: SupplierPerformanceOverview[];
  purchaseTrends: PurchaseTrend[];
  supplierBalanceAnalysis: SupplierBalanceAnalysis[];
  costAnalysis: CostAnalysis[];
  salesTrends: SalesTrend[];
  customerAnalysis: CustomerAnalysis[];
  orderStatusBreakdown: { status: string; count: number; value: number }[];
  inventoryHealth: InventoryHealth;
  stockLevelAnalysis: StockLevelAnalysis;
  materialConsumption: MaterialConsumption;
  storePerformance: StorePerformance[];
  productionEfficiency: ProductionEfficiency;
  wipAnalysis: WIPAnalysis;
  materialUtilization: MaterialUtilization;
  processPerformance: ProcessPerformance;
  operationalKPIs: OperationalKPI;
  monthlyQuantitySummary: MonthlyQuantitySummary[];
  alerts: {
    overduePOs: number;
    overdueWorkOrders: number;
    lowStockAlerts: number;
    qualityIssues: number;
  };
  lastUpdated: string;
}

// Executive Dashboard Interfaces
export interface PurchaseSpendAnalysis {
  currentMonth: number;
  yearToDate: number;
  previousPeriod: number;
  trend: PurchaseTrend[];
  byBranch: { branchName: string; value: number }[];
  bySupplier: { supplierName: string; value: number }[];
  byCategory: { categoryName: string; value: number }[];
}

export interface SalesRevenueAnalysis {
  currentMonth: number;
  yearToDate: number;
  previousPeriod: number;
  trend: SalesTrend[];
  byBranch: { branchName: string; value: number }[];
  byCustomer: { customerName: string; value: number }[];
  byCategory: { categoryName: string; value: number }[];
}

export interface InventoryValueAnalysis {
  totalInventoryValue: number;
  byBranch: { branchName: string; value: number }[];
  byCategory: { categoryName: string; value: number }[];
  trend: { month: string; monthName: string; value: number }[];
  carryingCost: number;
}

export interface WIPValueAnalysis {
  totalWIPValue: number;
  byBranch: { branchName: string; value: number }[];
  trend: { month: string; monthName: string; value: number }[];
  wipAsPercentOfInventory: number;
}

export interface SupplierPerformanceSummary {
  averagePerformanceScore: number;
  topSuppliers: SupplierPerformanceOverview[];
  bottomSuppliers: SupplierPerformanceOverview[];
  trend: { month: string; monthName: string; averageScore: number }[];
  totalActiveSuppliers: number;
}

export interface PurchaseEfficiencyMetrics {
  overallFulfillmentRate: number;
  byBranch: { branchName: string; rate: number }[];
  averageDeliveryTime: number;
  onTimeDeliveryPercentage: number;
  rejectionRate: { quantity: number; value: number };
}

export interface SalesPerformanceMetrics {
  orderFulfillmentRate: number;
  averageOrderValue: number;
  salesOrderConversionRate: number;
  customerRetentionRate: number;
  byBranch: { branchName: string; fulfillmentRate: number }[];
}

export interface ProductionEfficiencyMetrics {
  workOrderCompletionRate: number;
  averageProductionCycleTime: number;
  productionOutputTrend: { month: string; monthName: string; output: number }[];
  qualityMetrics: { rejectionRate: number; defectRate: number };
  byBranch: { branchName: string; completionRate: number }[];
}

export interface InventoryHealthMetrics {
  inventoryTurnoverRatio: number;
  daysOfInventoryOnHand: number;
  slowMovingInventoryValue: number;
  stockOutIncidents: number;
  byBranch: { branchName: string; turnoverRatio: number }[];
  byCategory: { categoryName: string; turnoverRatio: number }[];
}

export interface SupplyChainMetrics {
  averageLeadTime: number;
  supplierReliabilityScore: number;
  materialAvailabilityRate: number;
  byBranch: { branchName: string; leadTime: number }[];
  bySupplier: { supplierName: string; reliabilityScore: number }[];
}

export interface BranchPerformance {
  branchName: string;
  purchaseSpend: number;
  salesRevenue: number;
  inventoryValue: number;
  productionOutput: number;
  wipValue: number;
  performanceScore: number;
}

export interface BranchEfficiency {
  branchName: string;
  poFulfillmentRate: number;
  onTimeDeliveryRate: number;
  productionEfficiency: number;
  inventoryTurnover: number;
}

export interface TrendAnalysis {
  purchaseSpendTrend: PurchaseTrend[];
  salesRevenueTrend: SalesTrend[];
  inventoryValueTrend: { month: string; monthName: string; value: number }[];
  productionOutputTrend: { month: string; monthName: string; output: number }[];
  supplierPerformanceTrend: { month: string; monthName: string; averageScore: number }[];
}

export interface ForecastingIndicators {
  purchaseOrderPipeline: { count: number; value: number };
  salesOrderPipeline: { count: number; value: number };
  productionPipeline: { wipValue: number; workOrdersCount: number };
  expectedDeliveries: { count: number; value: number; next30Days: number };
}

export interface RiskIndicators {
  lowPerformanceSuppliers: SupplierPerformanceOverview[];
  highRejectionRateItems: { itemName: string; rejectionRate: number }[];
  overdueDeliveries: number;
  overdueWorkOrders: number;
  criticalLowStockItems: number;
}

export interface QualityMetrics {
  overallRejectionRate: number;
  bySupplier: { supplierName: string; rejectionRate: number }[];
  byCategory: { categoryName: string; rejectionRate: number }[];
  trend: { month: string; monthName: string; rejectionRate: number }[];
  isImproving: boolean;
}

export interface StrategicKPI {
  purchaseEfficiency: {
    poFulfillmentRate: number;
    onTimeDeliveryPercentage: number;
    averageDeliveryTime: number;
  };
  salesPerformance: {
    orderFulfillmentRate: number;
    averageOrderValue: number;
    customerSatisfaction: number;
  };
  inventoryManagement: {
    inventoryTurnover: number;
    daysOfInventory: number;
    stockOutRate: number;
  };
  production: {
    completionRate: number;
    cycleTime: number;
    qualityRate: number;
  };
  financial: {
    purchaseSpend: number;
    salesRevenue: number;
    inventoryValue: number;
    wipValue: number;
  };
  periodComparison: {
    monthOverMonth: { [key: string]: number };
    yearOverYear: { [key: string]: number };
  };
}

export interface ExecutiveSummaryCards {
  totalPurchaseSpend: { mtd: number; ytd: number; change: number };
  totalSalesRevenue: { mtd: number; ytd: number; change: number };
  totalInventoryValue: number;
  totalWIPValue: number;
  averageSupplierPerformanceScore: number;
  overallProductionEfficiency: number;
}

export interface ExecutiveDashboardData {
  purchaseSpendAnalysis: PurchaseSpendAnalysis;
  salesRevenueAnalysis: SalesRevenueAnalysis;
  inventoryValue: InventoryValueAnalysis;
  wipValue: WIPValueAnalysis;
  supplierPerformanceSummary: SupplierPerformanceSummary;
  purchaseEfficiency: PurchaseEfficiencyMetrics;
  salesPerformance: SalesPerformanceMetrics;
  productionEfficiency: ProductionEfficiencyMetrics;
  inventoryHealth: InventoryHealthMetrics;
  supplyChainMetrics: SupplyChainMetrics;
  branchPerformance: BranchPerformance[];
  branchEfficiency: BranchEfficiency[];
  trendAnalysis: TrendAnalysis;
  forecastingIndicators: ForecastingIndicators;
  riskIndicators: RiskIndicators;
  qualityMetrics: QualityMetrics;
  strategicKPIs: StrategicKPI;
  executiveSummaryCards: ExecutiveSummaryCards;
  lastUpdated: string;
}

// Export singleton instance
export const dashboardApiService = new DashboardApiService();
